import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix marker icon URLs
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function RecenterMap({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo([position.lat, position.lng], map.getZoom());
    }
  }, [position, map]);
  return null;
}

export default function LocationPickerMap({ onLocationChange }) {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const mapRef = useRef(null);
  const containerRef = useRef(null);

  // Detect user location
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const pos = { lat: latitude, lng: longitude };
        setMarkerPosition(pos);
        setLocationLoaded(true);
        onLocationChange?.(pos);
      },
      () => {
        const fallback = { lat: 20.5937, lng: 78.9629 };
        setMarkerPosition(fallback);
        setLocationLoaded(true);
        onLocationChange?.(fallback);
      },
      { enableHighAccuracy: true }
    );
  }, [onLocationChange]);

  // Invalidate Leaflet map size after mount
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 500); // delay ensures layout has stabilized
    }
  }, [locationLoaded]);

  const handleDragEnd = (e) => {
    const pos = e.target.getLatLng();
    const updated = { lat: pos.lat, lng: pos.lng };
    setMarkerPosition(updated);
    onLocationChange?.(updated);
  };

  if (!locationLoaded || !markerPosition) {
    return <p>📍 Loading map...</p>;
  }

  return (
    <div
      ref={containerRef}
      style={{
        height: "300px",
        width: "100%",
        marginBottom: "1rem",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <MapContainer
        center={[markerPosition.lat, markerPosition.lng]}
        zoom={15}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
        whenCreated={(mapInstance) => {
          mapRef.current = mapInstance;
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />
        <Marker
          position={[markerPosition.lat, markerPosition.lng]}
          draggable={true}
          eventHandlers={{ dragend: handleDragEnd }}
        >
          <Popup>Drag me to adjust location</Popup>
        </Marker>
        <RecenterMap position={markerPosition} />
      </MapContainer>
    </div>
  );
}